// src/components/common/Footer.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { FaFacebook, FaInstagram, FaTwitter, FaYoutube, FaGooglePlay, FaApple } from 'react-icons/fa';

// ✅ Import the same logo images used in Navbar
import logoWithText from '../../assets/images/Only Logo Text.png';
import logoOnly from '../../assets/images/Only Logo.png';

// ✅ App Download URL (same as Navbar)
const APP_DOWNLOAD_URL = 'https://play.google.com/store/apps/details?id=com.siancetiffin.app';

// ✅ Direct palette access (EXACT SAME as Navbar)
const palette = {
  orange: '#FD8A36',
  bgLightTop: '#FFF4E6',
  bgLightMain: '#FFFFFF',
  textLightDark: '#2E2218',
  borderLight: '#F5E6DA',
  bgDarkTop: '#1E1712',
  textDarkLight: '#F4EFEA',
  borderDark: '#2A2017',
};

const Footer = () => {
  // ✅ Get theme from localStorage (same as Navbar)
  const isDark = localStorage.getItem('theme') === 'dark';
  
  // ✅ EXACT SAME STYLES as Navbar
  const footerStyles = {
    light: {
      backgroundColor: palette.bgLightTop,
      borderTop: `1px solid ${palette.borderLight}`,
      boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
    },
    dark: {
      backgroundColor: palette.bgDarkTop,
      borderTop: `1px solid ${palette.borderDark}`,
    }
  };

  const currentStyle = isDark ? footerStyles.dark : footerStyles.light;
  const textColor = isDark ? palette.textDarkLight : palette.textLightDark;

  return (
    <footer 
      className="transition-colors duration-300"
      style={currentStyle}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* ===== COLUMN 1: Company Logo (EXACT SAME AS NAVBAR) ===== */}
          <div>
            <Link to="/" className="flex items-center gap-2 group">
              <img 
                src={logoOnly} 
                alt="Foodies Tiffin" 
                className="h-10 w-10 object-contain transition-transform duration-300 group-hover:scale-110"
                style={{ filter: isDark ? 'brightness(1.1)' : 'none' }}
              />
              <img 
                src={logoWithText} 
                alt="Foodies Tiffin" 
                className="h-8 object-contain transition-transform duration-300 group-hover:scale-105"
                style={{ filter: isDark ? 'brightness(1.1)' : 'none' }}
              />
            </Link>
            
            <p className="text-sm mt-3" style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}>
              Fresh meals delivered daily with love ❤️
            </p>
          </div>

          {/* ===== COLUMN 2: Quick Links ===== */}
          <div>
            <h4 className="font-semibold mb-3 text-sm" style={{ color: textColor }}>
              Quick Links
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link 
                  to="/" 
                  className="transition-colors duration-200 hover:opacity-80"
                  style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
                >
                  Home
                </Link>
              </li>
              <li>
                <Link 
                  to="/tiffin-service" 
                  className="transition-colors duration-200 hover:opacity-80"
                  style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
                >
                  Meals
                </Link>
              </li>
              <li>
                <Link 
                  to="/my-orders" 
                  className="transition-colors duration-200 hover:opacity-80"
                  style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
                >
                  Orders
                </Link>
              </li>
              <li>
                <Link 
                  to="/profile" 
                  className="transition-colors duration-200 hover:opacity-80"
                  style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
                >
                  Profile
                </Link>
              </li>
            </ul>
          </div>

          {/* ===== COLUMN 3: Support ===== */}
          <div>
            <h4 className="font-semibold mb-3 text-sm" style={{ color: textColor }}>
              Support
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link 
                  to="/about" 
                  className="transition-colors duration-200 hover:opacity-80"
                  style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link 
                  to="/faq" 
                  className="transition-colors duration-200 hover:opacity-80"
                  style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
                >
                  FAQ
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact" 
                  className="transition-colors duration-200 hover:opacity-80"
                  style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link 
                  to="/privacy" 
                  className="transition-colors duration-200 hover:opacity-80"
                  style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link 
                  to="/terms" 
                  className="transition-colors duration-200 hover:opacity-80"
                  style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link 
                  to="/delete-account-info" 
                  className="transition-colors duration-200 hover:opacity-80"
                  style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
                >
                  Delete Account
                </Link>
              </li>
            </ul>
          </div>

          {/* ===== COLUMN 4: Social Media ===== */}
          <div>
            <h4 className="font-semibold mb-3 text-sm" style={{ color: textColor }}>
              Follow Us
            </h4>
            <div className="flex gap-4 text-xl">
              <a 
                href="#" 
                className="transition-all duration-200 hover:scale-110"
                style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
                aria-label="Facebook"
              >
                <FaFacebook />
              </a>
              <a 
                href="#" 
                className="transition-all duration-200 hover:scale-110"
                style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
                aria-label="Instagram"
              >
                <FaInstagram />
              </a>
              <a 
                href="#" 
                className="transition-all duration-200 hover:scale-110"
                style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
                aria-label="Twitter"
              >
                <FaTwitter />
              </a>
              <a 
                href="#" 
                className="transition-all duration-200 hover:scale-110"
                style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
                aria-label="YouTube"
              >
                <FaYoutube />
              </a>
            </div>
            
            {/* Download App */}
            <div className="mt-4">
              <p className="text-xs" style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}>
                📱 Download our app
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <a
                  href={APP_DOWNLOAD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-all hover:scale-105"
                  style={{ backgroundColor: palette.orange }}
                >
                  <FaGooglePlay className="text-sm" />
                  Play Store
                </a>
                <button
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-all hover:scale-105"
                  style={{ backgroundColor: '#3B82F6' }}
                  onClick={() => alert('App Store link coming soon!')}
                >
                  <FaApple className="text-sm" />
                  App Store
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ===== FOOTER BOTTOM ===== */}
        <div 
          className="border-t mt-6 pt-4 flex flex-wrap justify-center items-center gap-3 text-sm"
          style={{ 
            borderColor: isDark ? palette.borderDark : palette.borderLight,
            color: isDark ? palette.textDarkLight : palette.textLightDark
          }}
        >
          <Link 
            to="/about" 
            className="transition-colors duration-200 hover:opacity-80"
            style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
          >
            About Us
          </Link>
          
          <span>|</span>
          
          <Link 
            to="/privacy" 
            className="transition-colors duration-200 hover:opacity-80"
            style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
          >
            Privacy Policy
          </Link>
          
          <span>|</span>
          
          <Link 
            to="/terms" 
            className="transition-colors duration-200 hover:opacity-80"
            style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
          >
            Terms of Service
          </Link>
          
          <span>|</span>
          
          <Link 
            to="/delete-account-info" 
            className="transition-colors duration-200 hover:opacity-80"
            style={{ color: isDark ? palette.textDarkLight : palette.textLightDark }}
          >
            Delete Account
          </Link>
        </div>

        {/* ===== COPYRIGHT ===== */}
        <div 
          className="border-t mt-4 pt-4 text-center text-sm"
          style={{ 
            borderColor: isDark ? palette.borderDark : palette.borderLight,
            color: isDark ? palette.textDarkLight : palette.textLightDark
          }}
        >
          <p>© 2026 Foodies Tiffin Service. All rights reserved.</p>
          <p className="mt-1">Made with ❤️ for food lovers</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;